import { getPaginatedStudioIntegrationConfigs } from '../../../api/studioIntegrationConfigsApi'
import LinkButton from '../../../components/link-button/link-button'
import StudioIntegrationConfigFilter from '../../../components/studio-integration-configs/studio-integration-configs-filter/studio-integration-configs-filter'
import StudioIntegrationSyncLessonDescriptionModal from '../../../components/studio-integration-configs/studio-integration-sync-lesson-description-modal/studio-integration-sync-lesson-description-modal'
import StudioIntegrationSyncLessonModal from '../../../components/studio-integration-configs/studio-integration-sync-lesson-modal/studio-integration-sync-lesson-modal'
import config from '../../../config'
import { getSearchParams, getSearchValues } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import {
  IPaginatedStudioIntegrationConfigFilterParams,
  IStudioIntegrationConfig,
  IStudioIntegrationConfigFilterParams,
  IStudioIntegrationConfigJson,
} from '../share-types'
import { PlusOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Col, Row, Space, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const { guavapassUrl } = config

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const StudioIntegrationConfigs = () => {
  const [studioIntegrationConfigs, setStudioIntegrationConfigs] = useState<
    IStudioIntegrationConfig[]
  >([])
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [searchValues, setSearchValues] = useState<IStudioIntegrationConfigFilterParams>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [syncLessonDescriptionModalVisible, setSyncLessonDescriptionModalVisible] =
    useState<boolean>(false)
  const [syncLessonModalVisible, setSyncLessonModalVisible] = useState<boolean>(false)

  useEffect(() => {
    const newSearchValues = getSearchValues(
      [
        { key: 'type', isBoolean: false },
        { key: 'size', isBoolean: false, default: defaultPage.pageSize },
        { key: 'page', isBoolean: false, default: defaultPage.currentPage - 1 },
      ],
      searchParams,
    )

    const data = {
      ...newSearchValues,
      size: Number(newSearchValues.size),
      page: Number(newSearchValues.page),
    }
    setSearchValues(newSearchValues)
    getPaginatedStudioIntegrationConfigsAction(data)
  }, [])

  const getPaginatedStudioIntegrationConfigsAction = (
    data: IPaginatedStudioIntegrationConfigFilterParams,
  ) => {
    getPaginatedStudioIntegrationConfigs(data).then(res => {
      if (res && res.pageInfo && res.data) {
        setPageInfo({
          ...res.pageInfo,
          currentPage: res.pageInfo.currentPage + 1,
        })
        setStudioIntegrationConfigs(res.data)
      }
    })
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    const data = {
      ...searchValues,
      size: pageSize,
      page: page - 1,
    }

    getPaginatedStudioIntegrationConfigsAction(data)
  }

  const onSearch = (value: IStudioIntegrationConfigFilterParams) => {
    const data = {
      size: pageInfo.pageSize,
      page: 0,
      ...value,
    }
    setSearchValues(value)
    setSearchParams(getSearchParams(value))
    getPaginatedStudioIntegrationConfigsAction(data)
  }

  const columns: ColumnsType<IStudioIntegrationConfig> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: translate('studioIntegrationConfig.integratorType'),
      dataIndex: 'integratorType',
      key: 'integratorType',
    },
    {
      title: translate('studioIntegrationConfig.organizationIds'),
      dataIndex: 'config',
      key: 'config',
      render: (config: IStudioIntegrationConfigJson) =>
        Array.from(config.organization_ids).join(', '),
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      render: (record: IStudioIntegrationConfig) => (
        <Space>
          <LinkButton to={`/studio-integration-config/${record.id}`}>
            {translate('view')}
          </LinkButton>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Space className="table-space" orientation="vertical" size={16}>
        <Row align="middle">
          <Col xs={{ span: 24 }} sm={{ span: 12 }} className="left-col">
            <div>{translate('navBar.studioIntegrationConfig')}</div>
          </Col>
          <AuthPermission permission={PermissionsEnum.STUDIO_INTEGRATION_WRITE}>
            <Col xs={{ span: 24 }} sm={{ span: 12 }} className="right-col">
              <Space>
                <Tooltip title="请移步至GP Admin创建Studio Integration Configs">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    href={`${guavapassUrl}/admin/studio_integration_configs/new`}
                  >
                    {translate('studioIntegrationConfig.createNew')}
                  </Button>
                </Tooltip>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={() => setSyncLessonDescriptionModalVisible(true)}
                >
                  {translate('studioIntegrationConfig.syncLessonDescriptions')}
                </Button>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={() => setSyncLessonModalVisible(true)}
                >
                  {translate('studioIntegrationConfig.syncLessons')}
                </Button>
              </Space>
            </Col>
          </AuthPermission>
        </Row>
        <Row className="filter-bar">
          <StudioIntegrationConfigFilter onSearch={onSearch} formData={searchValues} />
        </Row>

        <Table
          rowKey={record => record.id}
          columns={columns}
          dataSource={studioIntegrationConfigs}
          pagination={{
            current: pageInfo.currentPage,
            pageSize: pageInfo.pageSize,
            total: pageInfo.count,
            onChange: paginationOnChange,
          }}
        />
      </Space>

      {syncLessonDescriptionModalVisible && (
        <StudioIntegrationSyncLessonDescriptionModal
          visible={syncLessonDescriptionModalVisible}
          onCancel={() => setSyncLessonDescriptionModalVisible(false)}
          onSubmit={() => setSyncLessonDescriptionModalVisible(false)}
        />
      )}

      {syncLessonModalVisible && (
        <StudioIntegrationSyncLessonModal
          visible={syncLessonModalVisible}
          onCancel={() => setSyncLessonModalVisible(false)}
          onSubmit={() => setSyncLessonModalVisible(false)}
        />
      )}
    </>
  )
}

export default StudioIntegrationConfigs
